import { useCallback, useEffect, useRef, useState } from "react";
import { Pause, Play } from "@phosphor-icons/react";
import { FileMetadata } from "../../../models/FileMetadata";
import { formatDuration } from "../../../utils/time";
import { AudioView } from "./styles";
import { AnimatePresence, motion } from "motion/react";
import { useFileAccessURL } from "../../../hooks/useFileAccessURL";
import { getFileAccessReference } from "../../../utils/fileAccessURL";

export interface AudioProps {
  data: FileMetadata;
  currentAudio: string | null;
  audioRate: number;
  handlePlayAudio: ( id: string ) => void;
  handleStopAudio: () => void;
  handleNextAudioRate: () => void;
};

export const Audio = ({
  data,
  currentAudio,
  audioRate,
  handlePlayAudio,
  handleStopAudio,
  handleNextAudioRate,
}: AudioProps ) => {
  const audioRef = useRef<HTMLAudioElement>( null );
  const audioReference = getFileAccessReference(data, "inline", "audio");
  const audioURL = useFileAccessURL({
    mode: "inline",
    fileMetadataId: data.id,
    reference: audioReference,
  });

  const [ loading, setLoading ] = useState<boolean>( true );

  const [ isPlaying, setIsPlaying ] = useState( false );
  const [ progress, setProgress ] = useState( 0 );
  const [ progressTime, setProgressTime ] = useState( 0 );
  const [ mediaDuration, setMediaDuration ] = useState( 0 );
  const duration = data.duration ?? data.durationSeconds ?? mediaDuration;

  const togglePlay = () => {
    const audio = audioRef.current;
    if ( !audio ) return;

    if ( isPlaying ) {
      handleStopAudio();

      audio.pause();
    } else {
      handlePlayAudio( data.id ?? "");
      audio.play();
    }

    setIsPlaying(( prev ) => !prev );
  };

  const handleSeek = ( e: React.ChangeEvent<HTMLInputElement> ) => {
    const value = parseFloat( e.target.value );

    const audio = audioRef.current;

    if ( !audio ) return;

    const time = ( value / 100 ) * duration;

    audio.currentTime = time;

    setProgressTime( time );

    setProgress( value );
  };

  const stopAudio = useCallback(() => {
    if ( currentAudio !== data.id ) {
      const audio = audioRef.current;

      if ( !audio ) return;

      if ( isPlaying ) {
        audio.pause();

        setIsPlaying( false );
      }
    }
  }, [ currentAudio, data.id, isPlaying ]);

  const onEnded = () => {
    stopAudio();

    const audio = audioRef.current;

    if ( !audio ) return;

    audio.pause();

    audio.currentTime = 0;

    setProgressTime( 0 );

    setProgress( 0 );
  };

  const changeAudioRate = ( audioRate: number ) => {
    const audio = audioRef.current;

    if (!audio) return;

    audio.playbackRate = audioRate;
  };

  useEffect(() => {
    stopAudio();
  }, [ stopAudio ]);

  useEffect(() => {
    changeAudioRate( audioRate );
  }, [ audioRate ]);

  useEffect(() => {
    const audio = audioRef.current;

    if ( !audio ) return;

    let frameId: number;

    const update = () => {
      if ( audio && !audio.paused && !audio.ended ) {
        const percent = duration > 0 ? ( audio.currentTime / duration ) * 100 : 0;

        setProgress( parseFloat( percent.toFixed( 1 )));

        setProgressTime( audio.currentTime );

        frameId = requestAnimationFrame( update );
      }
    };

    const start = () => {
      frameId = requestAnimationFrame( update );
    };

    const stop = () => {
      cancelAnimationFrame( frameId );
    };

    audio.addEventListener("play", start );
    audio.addEventListener("pause", stop );
    audio.addEventListener("ended", () => {
      stop();

      setIsPlaying( false );
    });

    return () => {
      stop();

      audio.removeEventListener("play", start );

      audio.removeEventListener("pause", stop );
    };
  }, [ duration ]);

  return (
    <AudioView $progress={ progress }>
      <div className="actions">
        <button
          className="play-pause"
          onClick={ togglePlay }
          disabled={ loading }
        >
          {
            isPlaying
              ? <Pause size={ 24 } weight="duotone" />
              : <Play size={ 24 } weight="duotone" />
          }
        </button>

        <input
          type="range"
          min={ 0 }
          max={ 100 }
          step={ 0.1 }
          value={ progress || 0 }
          onChange={ handleSeek }
        />

        <div className="rate">
          <AnimatePresence>
            {
              isPlaying && (
                <motion.div
                  className="button-rate-view"
                  initial={{
                    width: 0,
                    opacity: 0,
                  }}
                  animate={{
                    width: 48,
                    opacity: 1,
                  }}
                  exit={{
                    width: 0,
                    opacity: 0,
                  }}
                  transition={{ duration: .2, delay: .2 }}
                >
                  <button onClick={ handleNextAudioRate }>
                    { audioRate.toFixed( 1 )}x
                  </button>
                </motion.div>
              )
            }
          </AnimatePresence>
        </div>

        <audio
          ref={ audioRef }
          src={ audioURL }
          onEnded={ onEnded }
          onLoadStart={() => setLoading( true )}
          onLoadedData={() => setLoading( false )}
          onLoadedMetadata={() => {
            setLoading( false );
            setMediaDuration( audioRef.current?.duration ?? 0 );
          }}
        />
      </div>

      <div className="times">
        <span className="progress">{ formatDuration( progressTime * 1000 )}</span>
        <span className="duration">{ formatDuration( duration * 1000 )}</span>
      </div>
    </AudioView>
  );
};
